import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import './VoiceEnroll.css';
import {
  callPredictAPI,
  createSaveRecording, createSubmitForm, createLoad,
  createStartSegmentRecording, createStartContinuousRecording
} from '../utils/voiceEnrollShared';
import { VoiceEnrollLoading, VoiceEnrollError, VoiceEnrollSuccess, VoiceEnrollErrorBanner } from '../components/VoiceEnrollStates';
import VoiceEnrollForm from '../components/VoiceEnrollForm';
import VoiceEnrollRecording from '../components/VoiceEnrollRecording';

export default function VoiceEnroll() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(true);
  const [formData, setFormData] = useState({ name: '', age: '', gender: 'Other' });
  const [formSaving, setFormSaving] = useState(false);
  const [mode, setMode] = useState(null);
  const [recording, setRecording] = useState(false);
  const [timer, setTimer] = useState(40);
  const [segmentCount, setSegmentCount] = useState(0);
  const [, setAllResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [, setCurrentSegment] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const autoStopRef = useRef(null);
  const resultsRef = useRef([]);

  useEffect(() => {
    const loadFn = createLoad({
      endpointSuffix: 'voice-enroll',
      token,
      setData: setStudentData,
      setSaved,
      setShowForm,
      setFormData,
      setError,
      setLoading,
      logPrefix: 'VoiceEnroll'
    });
    loadFn();
  }, [token]);

  const saveRecordingFn = createSaveRecording({
    endpointSuffix: 'voice-enroll',
    token, setSaved, setError, setSaving
  });

  const submitFormFn = createSubmitForm({
    endpointSuffix: 'voice-enroll',
    token, formData, setShowForm, setError, setFormSaving
  });

  const startSegmentRecording = createStartSegmentRecording({
    saveRecording: saveRecordingFn, callPredictAPI, resultsRef,
    setAllResults, setSegmentCount, setCurrentSegment, setRecording,
    setTimer, streamRef, mediaRecorderRef, audioChunksRef,
    intervalRef, autoStopRef, setError
  });

  const startContinuousRecording = createStartContinuousRecording({
    saveRecording: saveRecordingFn, callPredictAPI, setAllResults,
    setRecording, setTimer, streamRef, mediaRecorderRef, audioChunksRef,
    intervalRef, autoStopRef, setError
  });

  const startRecording = async (recordingMode) => {
    if (!studentData || studentData.voice_recorded) return;
    setMode(recordingMode);
    setError('');

    if (recordingMode === '40s') {
      await startContinuousRecording();
    } else {
      await startSegmentRecording();
    }
  };

  const continueToNextSegment = async () => {
    if (segmentCount < 5) {
      await startSegmentRecording();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  const resetRecording = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    setRecording(false);
    setTimer(40);
    setSegmentCount(0);
    setCurrentSegment(0);
    setAllResults([]);
    resultsRef.current = [];
    setMode(null);
  };

  if (loading) return <VoiceEnrollLoading />;
  if (error && !studentData) return <VoiceEnrollError message={error} />;
  if (saved) {
    return (
      <VoiceEnrollSuccess
        name={studentData?.student_name || formData.name}
        message="Your university can now view your emotional well-being assessment."
      />
    );
  }

  if (showForm) {
    return (
      <>
        <VoiceEnrollErrorBanner message={error} />
        <VoiceEnrollForm
          title="Student Registration"
          subtitle="Please fill in your details before recording voice"
          formData={formData}
          setFormData={setFormData}
          fields={[
            { name: 'name', label: 'Full Name', type: 'text', placeholder: 'Enter your full name', required: true },
            { name: 'age', label: 'Age', type: 'number', placeholder: 'Enter your age' },
            { name: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'] }
          ]}
          onSubmit={submitFormFn}
          submitting={formSaving}
          submitLabel="Continue to Voice Recording"
        />
      </>
    );
  }

  return (
    <>
      <VoiceEnrollErrorBanner message={error} />
      <VoiceEnrollRecording
        name={formData.name}
        mode={mode}
        recording={recording}
        timer={timer}
        segmentCount={segmentCount}
        onRecord={startRecording}
        onStop={stopRecording}
        onContinue={continueToNextSegment}
        onReset={resetRecording}
        saving={saving}
      />
    </>
  );
}
